import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");
const statuses = [
  "",
  "active",
  "suspended",
  "completed",
  "stopped",
  "ineligible",
  "migrated",
];
const deliveryStatuses = [
  "",
  "pending",
  "queued",
  "sending",
  "sent",
  "failed",
  "skipped",
  "carried_forward",
];

function Pager({ page, pageInfo, previous, next }) {
  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      spacing={1}
      mt={2}
    >
      <Button disabled={page === 0} onClick={previous}>
        Previous
      </Button>
      <Typography variant="body2">Page {page + 1}</Typography>
      <Button disabled={!pageInfo?.hasNextPage} onClick={next}>
        Next
      </Button>
    </Stack>
  );
}

export default function WorkflowMonitoring({
  workflow,
  api,
  versionId,
  refreshKey = 0,
}) {
  const [tab, setTab] = useState("participants");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [diagnostic, setDiagnostic] = useState("");
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState([null]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [metrics, setMetrics] = useState(null);
  const resetPage = () => {
    setPage(0);
    setCursors([null]);
  };
  const request = useRef(0);
  const load = useCallback(async () => {
    const token = ++request.current;
    setError("");
    try {
      const response =
        tab === "participants"
          ? await api.getEnrollments(
              workflow.id,
              status,
              email,
              10,
              cursors[page],
              versionId,
            )
          : await api.getDeliveries(
              workflow.id,
              status,
              email,
              diagnostic,
              10,
              cursors[page],
              versionId,
            );
      if (token !== request.current) return;
      setItems((response.edges || []).map((edge) => edge.node));
      setPageInfo(response.pageInfo || {});
      const result = await api.getOperationalMetrics(workflow.id, versionId);
      if (token === request.current) setMetrics(result);
    } catch (requestError) {
      if (token === request.current) setError(requestError.message);
    }
  }, [
    api,
    cursors,
    diagnostic,
    email,
    page,
    status,
    tab,
    workflow.id,
    versionId,
    refreshKey,
  ]);
  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => {
      clearTimeout(timer);
      request.current += 1;
    };
  }, [load]);
  const next = () => {
    setCursors((values) => {
      const copy = [...values];
      copy[page + 1] = pageInfo.endCursor;
      return copy;
    });
    setPage((value) => value + 1);
  };
  const retry = async (delivery) => {
    try {
      const result = await api.retryDelivery(delivery.id);
      enqueueSnackbar(result.message, {
        variant: result.success ? "success" : "error",
      });
      if (result.success) await load();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5">Monitoring</Typography>
      {metrics && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap my={2}>
          <Chip label={`${metrics.enrolled} enrolled`} />
          <Chip label={`${metrics.active} active`} color="primary" />
          <Chip label={`${metrics.suspended || 0} suspended`} />
          <Chip label={`${metrics.completed} completed`} />
          <Chip label={`${metrics.migrated || 0} migrated`} />
          <Chip label={`${metrics.suppressed} suppressed`} />
          <Chip label={`${metrics.stageExits} stage exits`} />
          <Chip label={`${metrics.deliveryStatuses?.sent || 0} sent`} />
          <Chip
            label={`${metrics.deliveryStatuses?.failed || 0} failed`}
            color={metrics.deliveryStatuses?.failed ? "error" : "default"}
          />
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Tabs
        value={tab}
        onChange={(_, value) => {
          setTab(value);
          setStatus("");
          resetPage();
        }}
      >
        <Tab value="participants" label="Participants" />
        <Tab value="deliveries" label="Deliveries" />
      </Tabs>
      <Grid container spacing={1} my={1}>
        <Grid size={{ xs: 12, md: 5 }}>
          <TextField
            fullWidth
            size="small"
            label="Search email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              resetPage();
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              resetPage();
            }}
          >
            {(tab === "participants" ? statuses : deliveryStatuses).map(
              (value) => (
                <MenuItem key={value || "all"} value={value}>
                  {value || "All statuses"}
                </MenuItem>
              ),
            )}
          </TextField>
        </Grid>
        {tab === "deliveries" && (
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Diagnostic code"
              value={diagnostic}
              onChange={(event) => {
                setDiagnostic(event.target.value);
                resetPage();
              }}
            />
          </Grid>
        )}
      </Grid>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {tab === "participants" ? (
                <>
                  <TableCell>Participant</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Current stage</TableCell>
                  <TableCell>Enrolled</TableCell>
                  <TableCell />
                </>
              ) : (
                <>
                  <TableCell>Participant</TableCell>
                  <TableCell>Stage / email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scheduled</TableCell>
                  <TableCell>Diagnostic</TableCell>
                  <TableCell />
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover>
                {tab === "participants" ? (
                  <>
                    <TableCell>
                      {item.recipientName}
                      <Typography variant="caption" display="block">
                        {item.recipientEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.recipientSchool}
                      {item.recipientSchoolPhase && (
                        <Typography variant="caption" display="block">
                          {item.recipientSchoolPhase}
                        </Typography>
                      )}
                      {item.recipientSubjects?.length > 0 && (
                        <Typography variant="caption" display="block">
                          {item.recipientSubjects
                            .map((subject) => subject.name)
                            .join(", ")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={item.status} />
                    </TableCell>
                    <TableCell>{item.currentStage?.name || "—"}</TableCell>
                    <TableCell>{formatDate(item.enrolledAt)}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => setSelected(item)}>
                        History
                      </Button>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{item.recipientEmail}</TableCell>
                    <TableCell>
                      {item.stageName}
                      <Typography variant="caption" display="block">
                        {item.templateName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.status}
                        color={item.status === "failed" ? "error" : "default"}
                      />
                    </TableCell>
                    <TableCell>{formatDate(item.scheduledFor)}</TableCell>
                    <TableCell>{item.diagnosticCode || "—"}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        disabled={!item.retryable}
                        title={item.retryUnavailableReason}
                        onClick={() => retry(item)}
                      >
                        Retry
                      </Button>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
            {!items.length && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No matching records.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager
        page={page}
        pageInfo={pageInfo}
        previous={() => setPage((value) => value - 1)}
        next={next}
      />
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Participant history</DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2} mt={1}>
              <Typography>
                <strong>{selected.recipientEmail}</strong> · {selected.status}
              </Typography>
              {selected.suspensionReason && (
                <Typography>
                  Suspended: {selected.suspensionReason.replaceAll("_", " ")}.
                  Resume requires an active, eligible user and a live workflow
                  version.
                </Typography>
              )}
              {selected.continuationHistory?.length > 0 && (
                <>
                  <Typography variant="h6">Earlier versions</Typography>
                  {selected.continuationHistory.map((entry) => (
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5 }}
                      key={entry.enrollment_id}
                    >
                      <Typography fontWeight={700}>
                        Version {entry.version_number} · {entry.status}
                      </Typography>
                      {entry.deliveries.map((delivery) => (
                        <Typography key={delivery.id}>
                          {delivery.name} ·{" "}
                          {delivery.status.replaceAll("_", " ")} ·{" "}
                          {formatDate(delivery.sent_at)}
                        </Typography>
                      ))}
                    </Paper>
                  ))}
                </>
              )}
              <Typography variant="h6">Stage visits</Typography>
              {selected.stageHistory.map((visit) => (
                <Paper variant="outlined" sx={{ p: 1.5 }} key={visit.id}>
                  <Typography fontWeight={700}>
                    {visit.sequence}. {visit.stage.name}
                  </Typography>
                  <Typography variant="body2">
                    Entered {formatDate(visit.enteredAt)} · Exited{" "}
                    {formatDate(visit.exitedAt)}
                  </Typography>
                  {visit.exitReason && (
                    <Typography variant="body2">{visit.exitReason}</Typography>
                  )}
                </Paper>
              ))}
              <Typography variant="h6">Deliveries</Typography>
              {selected.deliveries.map((delivery) => (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  key={delivery.id}
                >
                  <Typography>{delivery.templateName}</Typography>
                  <Chip size="small" label={delivery.status} />
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
